/**
 * PUBLIC server function that ingests batched analytics events into
 * `customer_events` and rolls up per-session aggregates into
 * `visitor_sessions`. Rate-limited by anonymous id.
 *
 * Deduplication: `event_id` has a unique index; we upsert with
 * `ignoreDuplicates: true` so a client retry is safe.
 */
import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";

const EventSchema = z.object({
  eventId: z.string().min(8).max(64),
  eventName: z.string().min(1).max(64),
  sessionId: z.string().min(8).max(64),
  pageViewId: z.string().min(8).max(64).optional().nullable(),
  pagePath: z.string().max(255).optional().nullable(),
  pageType: z.string().max(64).optional().nullable(),
  productId: z.string().max(64).optional().nullable(),
  properties: z.record(z.string(), z.unknown()).optional(),
  occurredAt: z.string().min(10).max(40),
});

const SessionMetaSchema = z.object({
  sessionId: z.string().min(8).max(64),
  startedAt: z.string().min(10).max(40),
  landingPage: z.string().max(255).optional().nullable(),
  referrer: z.string().max(512).optional().nullable(),
  utm: z
    .object({
      utm_source: z.string().max(120).nullable().optional(),
      utm_medium: z.string().max(120).nullable().optional(),
      utm_campaign: z.string().max(120).nullable().optional(),
      utm_content: z.string().max(120).nullable().optional(),
      utm_term: z.string().max(120).nullable().optional(),
    })
    .optional(),
  deviceType: z.enum(["mobile", "tablet", "desktop"]).optional().nullable(),
  browserFamily: z.string().max(64).optional().nullable(),
});

const InputSchema = z.object({
  anonymousId: z.string().min(8).max(128),
  session: SessionMetaSchema,
  events: z.array(EventSchema).min(1).max(50),
  summary: z
    .object({
      maxScrollPercent: z.number().min(0).max(100).optional(),
      activeTimeMs: z.number().int().min(0).max(24 * 60 * 60 * 1000).optional(),
      totalTimeMs: z.number().int().min(0).max(24 * 60 * 60 * 1000).optional(),
      exitPage: z.string().max(255).optional().nullable(),
      engaged: z.boolean().optional(),
    })
    .optional(),
});

export const ingestEventsFn = createServerFn({ method: "POST" })
  .inputValidator((d: unknown) => InputSchema.parse(d))
  .handler(async ({ data }) => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");

    // First-party operational telemetry (own database, no third parties) is
    // essential processing and is not gated on optional analytics consent.

    // rate limit — generous, we ship batches
    const { data: rl } = await supabaseAdmin.rpc("check_rate_limit", {
      _bucket: `ci:ingest:${data.anonymousId.slice(0, 32)}`,
      _limit: 60,
      _window_seconds: 60,
    });
    const rlRow = Array.isArray(rl) ? rl[0] : rl;
    if (rlRow && rlRow.allowed === false) {
      return { ok: false, reason: "rate_limited" as const };
    }

    const { ensureAnonymousProfile } = await import("./identity.server");
    const profileId = await ensureAnonymousProfile(data.anonymousId);

    // 1) upsert session (best-effort; the row is source of truth for aggregates)
    const s = data.session;
    const { getRequestHeader } = await import("@tanstack/react-start/server");
    const { geoFromHeaders } = await import("@/lib/geo/visitor-geo");
    const geo = geoFromHeaders((n) => getRequestHeader(n));
    const nowIso = new Date().toISOString();
    const sessionRow = {
      session_id: s.sessionId,
      profile_id: profileId,
      anonymous_id: data.anonymousId,
      started_at: s.startedAt,
      last_activity_at: nowIso,
      landing_page: s.landingPage ?? null,
      referrer: s.referrer ?? null,
      utm_source: s.utm?.utm_source ?? null,
      utm_medium: s.utm?.utm_medium ?? null,
      utm_campaign: s.utm?.utm_campaign ?? null,
      utm_content: s.utm?.utm_content ?? null,
      utm_term: s.utm?.utm_term ?? null,
      device_type: s.deviceType ?? null,
      browser_family: s.browserFamily ?? null,
      ...(geo.country ? { country_code: geo.country } : {}),
    };
    const { error: sessionError } = await supabaseAdmin
      .from("visitor_sessions")
      .upsert(sessionRow as never, { onConflict: "session_id", ignoreDuplicates: false });
    if (sessionError) return { ok: false, reason: "session_not_saved" as const };

    // 2) insert events (dedup on event_id unique index)
    const rows = data.events.map((e) => ({
      event_id: e.eventId,
      profile_id: profileId,
      anonymous_id: data.anonymousId,
      session_id: e.sessionId,
      page_view_id: e.pageViewId ?? null,
      event_name: e.eventName,
      page_path: e.pagePath ?? null,
      page_type: e.pageType ?? null,
      product_id: e.productId ?? null,
      properties: (e.properties ?? {}) as never,
      occurred_at: e.occurredAt,
    }));
    if (geo.city || geo.region) {
      rows.push({
        event_id: `geo-${s.sessionId}`.slice(0, 64),
        profile_id: profileId,
        anonymous_id: data.anonymousId,
        session_id: s.sessionId,
        page_view_id: null,
        event_name: "session_geo",
        page_path: s.landingPage ?? null,
        page_type: null,
        product_id: null,
        properties: { city: geo.city, region: geo.region, country: geo.country } as never,
        occurred_at: s.startedAt,
      });
    }
    const { data: insertedEvents, error: evErr } = await supabaseAdmin
      .from("customer_events")
      .upsert(rows as never, { onConflict: "event_id", ignoreDuplicates: true }).select("event_id");
    if (evErr) {
      // don't leak internals — but surface the failure
      return { ok: false, reason: "insert_failed" as const };
    }

    // 2b) persist per-(visitor, session, product) engagement summaries.
    // Idempotent by design: values only ever grow server-side, so client
    // retries and page refreshes collapse into one row per dedup key.
    const engagementEvents = data.events
      .filter((e) => e.eventName === "product_engagement")
      .map((e) => ({
        sessionId: e.sessionId,
        pageViewId: e.pageViewId ?? null,
        occurredAt: e.occurredAt,
        properties: e.properties,
      }));
    if (engagementEvents.length > 0) {
      const { upsertProductEngagement } = await import("./engagement.server");
      await upsertProductEngagement(supabaseAdmin, data.anonymousId, engagementEvents);
    }

    // 3) roll up per-session aggregates
    if (data.summary) {
      const sum = data.summary;
      // Fetch current so we only ever grow the max/active/total.
      const { data: current } = await supabaseAdmin
        .from("visitor_sessions")
        .select("max_scroll_percent, active_time_ms, total_time_ms, engaged")
        .eq("session_id", s.sessionId)
        .maybeSingle();

      const nextMax = Math.max(
        Number(current?.max_scroll_percent ?? 0),
        Number(sum.maxScrollPercent ?? 0),
      );
      const nextActive = Math.max(
        Number(current?.active_time_ms ?? 0),
        Number(sum.activeTimeMs ?? 0),
      );
      const nextTotal = Math.max(
        Number(current?.total_time_ms ?? 0),
        Number(sum.totalTimeMs ?? 0),
      );
      const nextEngaged = Boolean(current?.engaged) || Boolean(sum.engaged);

      await supabaseAdmin
        .from("visitor_sessions")
        .update({
          max_scroll_percent: nextMax,
          active_time_ms: nextActive,
          total_time_ms: nextTotal,
          engaged: nextEngaged,
          exit_page: sum.exitPage ?? undefined,
          last_activity_at: nowIso,
        } as never)
        .eq("session_id", s.sessionId);
    }

    const accepted = insertedEvents?.length ?? 0;
    return { ok: true, profileId, submitted: rows.length, accepted, duplicates: rows.length - accepted };
  });
